/**
 * Academic Session manager (super_admin)
 * + Fee configuration manager (component × class × session)
 * + Trust extended info editor
 * + Hostel ↔ School mapping editor
 * All grouped on one page with tabs.
 */
import React, { useEffect, useState } from "react";
import DashboardLayout from "@/components/DashboardLayout";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Plus, Star, Trash, FloppyDisk, Buildings, House, GraduationCap } from "@phosphor-icons/react";
import { toast } from "sonner";

const fmt = (n) => new Intl.NumberFormat("en-IN", { style: "currency", currency: "INR", maximumFractionDigits: 0 }).format(Number(n) || 0);
const COMPONENTS = ["admission", "tuition", "hostel", "transport", "exam", "uniform", "lab", "other"];
const GRADES = ["junior", "primary", "secondary", "senior"];

function AcademicSessions() {
  const [rows, setRows] = useState([]);
  const [form, setForm] = useState({ name: "", start_date: "", end_date: "", is_current: false });
  const [busy, setBusy] = useState(false);
  const refresh = () => api.get("/admin/academic-sessions").then((r) => setRows(r.data || []));
  useEffect(() => { refresh().catch(() => {}); }, []);
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/admin/academic-sessions", form);
      setForm({ name: "", start_date: "", end_date: "", is_current: false });
      refresh();
      toast.success("Session created");
    } catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail) || e.message); }
    finally { setBusy(false); }
  };
  const setCurrent = async (id) => {
    try { await api.post(`/admin/academic-sessions/${id}/set-current`); refresh(); toast.success("Active session updated"); }
    catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail) || e.message); }
  };
  return (
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mt-4">
      <form onSubmit={submit} className="vs-card p-5 space-y-3" data-testid="session-form">
        <div className="eyebrow flex items-center gap-1.5"><Plus size={12} /> Add academic session</div>
        <div><Label>Name *</Label><Input data-testid="ses-name" required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="2026-27" className="bg-white" /></div>
        <div className="grid grid-cols-2 gap-2">
          <div><Label>Start</Label><Input data-testid="ses-start" type="date" value={form.start_date} onChange={(e) => setForm({ ...form, start_date: e.target.value })} className="bg-white" /></div>
          <div><Label>End</Label><Input data-testid="ses-end" type="date" value={form.end_date} onChange={(e) => setForm({ ...form, end_date: e.target.value })} className="bg-white" /></div>
        </div>
        <label className="flex items-center gap-2 text-xs"><input data-testid="ses-current" type="checkbox" checked={form.is_current} onChange={(e) => setForm({ ...form, is_current: e.target.checked })} /> Mark as current</label>
        <Button type="submit" disabled={busy} data-testid="ses-submit" className="bg-[var(--vs-secondary)] text-[var(--vs-bg)]">{busy ? "…" : "Create session"}</Button>
      </form>
      <div className="vs-card overflow-x-auto lg:col-span-2">
        <table className="w-full text-sm">
          <thead className="bg-[var(--vs-bg-subtle)]"><tr className="text-left text-[11px] uppercase font-mono tracking-widest text-[var(--vs-text-secondary)]"><th className="px-4 py-3">Name</th><th className="px-4 py-3">Range</th><th className="px-4 py-3">Status</th><th className="px-4 py-3"></th></tr></thead>
          <tbody>
            {rows.map((s) => (
              <tr key={s.id} className="border-t border-[var(--vs-border)]">
                <td className="px-4 py-3 font-mono">{s.name}</td>
                <td className="px-4 py-3 text-xs">{s.start_date || "—"} → {s.end_date || "—"}</td>
                <td className="px-4 py-3">{s.is_current ? <Badge variant="outline" className="border-[var(--vs-success)]/40 text-[var(--vs-success)]"><Star size={10} weight="fill" className="mr-1" />CURRENT</Badge> : <Badge variant="outline">archived</Badge>}</td>
                <td className="px-4 py-3 text-right">{!s.is_current && <Button size="sm" variant="outline" onClick={() => setCurrent(s.id)} data-testid={`ses-set-current-${s.name}`}>Make current</Button>}</td>
              </tr>
            ))}
            {rows.length === 0 && <tr><td colSpan={4} className="px-4 py-10 text-center text-[var(--vs-text-secondary)]">No sessions yet.</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function FeeConfigs() {
  const [sessions, setSessions] = useState([]);
  const [activeSession, setActiveSession] = useState("");
  const [rows, setRows] = useState([]);
  const [form, setForm] = useState({ grade: "primary", class_name: "", component: "tuition", amount: "" });
  const [busy, setBusy] = useState(false);
  const refresh = () => activeSession && api.get(`/admin/fee-configs?academic_session_id=${activeSession}`).then((r) => setRows(r.data || []));
  useEffect(() => {
    api.get("/admin/academic-sessions").then((r) => {
      setSessions(r.data || []);
      const cur = (r.data || []).find((s) => s.is_current);
      if (cur) setActiveSession(cur.id);
    });
  }, []);
  useEffect(() => { refresh(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [activeSession]);
  const submit = async (e) => {
    e.preventDefault();
    if (!activeSession) { toast.error("Pick an academic session"); return; }
    setBusy(true);
    try {
      await api.post("/admin/fee-configs", { ...form, academic_session_id: activeSession, amount: Number(form.amount) });
      setForm({ grade: form.grade, class_name: "", component: form.component, amount: "" });
      refresh();
      toast.success("Fee config saved");
    } catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail) || e.message); }
    finally { setBusy(false); }
  };
  const remove = async (id) => {
    if (!window.confirm("Delete this fee config?")) return;
    try { await api.delete(`/admin/fee-configs/${id}`); refresh(); toast.success("Deleted"); }
    catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail) || e.message); }
  };
  return (
    <div className="space-y-4 mt-4">
      <div className="vs-card p-4 flex items-center gap-3 flex-wrap">
        <div className="eyebrow">Academic session</div>
        <Select value={activeSession} onValueChange={setActiveSession}>
          <SelectTrigger className="w-60" data-testid="fc-session-picker"><SelectValue placeholder="Pick session" /></SelectTrigger>
          <SelectContent>{sessions.map((s) => <SelectItem key={s.id} value={s.id}>{s.name}{s.is_current ? " (current)" : ""}</SelectItem>)}</SelectContent>
        </Select>
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <form onSubmit={submit} className="vs-card p-5 space-y-3" data-testid="fc-form">
          <div className="eyebrow">Add / update fee row</div>
          <div className="grid grid-cols-2 gap-2">
            <div><Label>Grade</Label>
              <Select value={form.grade} onValueChange={(v) => setForm({ ...form, grade: v })}>
                <SelectTrigger data-testid="fc-grade" className="bg-white capitalize"><SelectValue /></SelectTrigger>
                <SelectContent>{GRADES.map((g) => <SelectItem key={g} value={g} className="capitalize">{g}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div><Label>Class *</Label><Input data-testid="fc-class" required value={form.class_name} onChange={(e) => setForm({ ...form, class_name: e.target.value })} className="bg-white" placeholder="e.g. 8" /></div>
          </div>
          <div><Label>Component</Label>
            <Select value={form.component} onValueChange={(v) => setForm({ ...form, component: v })}>
              <SelectTrigger data-testid="fc-component" className="bg-white capitalize"><SelectValue /></SelectTrigger>
              <SelectContent>{COMPONENTS.map((c) => <SelectItem key={c} value={c} className="capitalize">{c}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div><Label>Amount (INR) *</Label><Input data-testid="fc-amount" required type="number" min="0" step="1" value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })} className="bg-white font-mono" /></div>
          <Button type="submit" disabled={busy} data-testid="fc-submit" className="bg-[var(--vs-secondary)] text-[var(--vs-bg)]"><FloppyDisk size={12} className="mr-1.5" />{busy ? "Saving…" : "Save"}</Button>
        </form>
        <div className="vs-card overflow-x-auto lg:col-span-2">
          <table className="w-full text-sm">
            <thead className="bg-[var(--vs-bg-subtle)]"><tr className="text-left text-[11px] uppercase font-mono tracking-widest text-[var(--vs-text-secondary)]"><th className="px-4 py-3">Class</th><th className="px-4 py-3">Grade</th><th className="px-4 py-3">Component</th><th className="px-4 py-3 text-right">Amount</th><th className="px-4 py-3"></th></tr></thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.id} className="border-t border-[var(--vs-border)]">
                  <td className="px-4 py-3 font-mono">{r.class_name}</td>
                  <td className="px-4 py-3 capitalize">{r.grade}</td>
                  <td className="px-4 py-3 capitalize">{r.component}</td>
                  <td className="px-4 py-3 text-right font-mono">{fmt(r.amount)}</td>
                  <td className="px-4 py-3 text-right"><Button size="sm" variant="ghost" onClick={() => remove(r.id)} data-testid={`fc-delete-${r.id}`}><Trash size={12} className="text-[var(--vs-danger)]" /></Button></td>
                </tr>
              ))}
              {rows.length === 0 && <tr><td colSpan={5} className="px-4 py-10 text-center text-[var(--vs-text-secondary)]">No fee configs for this session.</td></tr>}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

function TrustInfo() {
  const [trusts, setTrusts] = useState([]);
  const [picked, setPicked] = useState("");
  const [form, setForm] = useState({ pan_no: "", reg_12a_no: "", reg_80g_no: "", address: "", phone: "", signatory_name: "", signatory_designation: "", email: "", website: "" });
  const [busy, setBusy] = useState(false);
  useEffect(() => { api.get("/admin/trusts").then((r) => { setTrusts(r.data || []); if (r.data?.[0]) setPicked(r.data[0].id); }); }, []);
  useEffect(() => {
    if (!picked) return;
    const t = trusts.find((x) => x.id === picked) || {};
    setForm({
      pan_no: t.pan_no || "", reg_12a_no: t.reg_12a_no || "", reg_80g_no: t.reg_80g_no || "",
      address: t.address || "", phone: t.phone || "",
      signatory_name: t.signatory_name || "", signatory_designation: t.signatory_designation || "",
      email: t.email || "", website: t.website || "",
    });
  }, [picked, trusts]);
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.patch(`/admin/trusts/${picked}/info`, form);
      toast.success("Trust info saved");
    } catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail) || e.message); }
    finally { setBusy(false); }
  };
  const uploadLogo = async (file) => {
    if (!file) return;
    try {
      const fd = new FormData(); fd.append("file", file);
      await api.post(`/admin/trusts/${picked}/logo`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Logo uploaded");
    } catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail) || e.message); }
  };
  return (
    <div className="space-y-4 mt-4">
      <div className="vs-card p-4 flex items-center gap-3">
        <div className="eyebrow">Trust</div>
        <Select value={picked} onValueChange={setPicked}>
          <SelectTrigger className="w-60" data-testid="trust-info-picker"><SelectValue /></SelectTrigger>
          <SelectContent>{trusts.map((t) => <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>)}</SelectContent>
        </Select>
      </div>
      <form onSubmit={submit} className="vs-card p-5 grid grid-cols-1 md:grid-cols-2 gap-4" data-testid="trust-info-form">
        <div><Label>PAN</Label><Input data-testid="ti-pan" value={form.pan_no} onChange={(e) => setForm({ ...form, pan_no: e.target.value.toUpperCase() })} maxLength={10} className="bg-white font-mono" /></div>
        <div><Label>12A registration #</Label><Input data-testid="ti-12a" value={form.reg_12a_no} onChange={(e) => setForm({ ...form, reg_12a_no: e.target.value })} className="bg-white font-mono" /></div>
        <div><Label>80G registration #</Label><Input data-testid="ti-80g" value={form.reg_80g_no} onChange={(e) => setForm({ ...form, reg_80g_no: e.target.value })} className="bg-white font-mono" /></div>
        <div><Label>Phone</Label><Input data-testid="ti-phone" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className="bg-white font-mono" /></div>
        <div><Label>Email</Label><Input data-testid="ti-email" type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="bg-white" /></div>
        <div><Label>Website</Label><Input data-testid="ti-website" value={form.website} onChange={(e) => setForm({ ...form, website: e.target.value })} className="bg-white" /></div>
        <div className="md:col-span-2"><Label>Registered address</Label><Textarea data-testid="ti-address" value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} rows={2} className="bg-white" /></div>
        <div><Label>Authorised signatory name</Label><Input data-testid="ti-sig-name" value={form.signatory_name} onChange={(e) => setForm({ ...form, signatory_name: e.target.value })} className="bg-white" /></div>
        <div><Label>Designation</Label><Input data-testid="ti-sig-role" value={form.signatory_designation} onChange={(e) => setForm({ ...form, signatory_designation: e.target.value })} className="bg-white" /></div>
        <div className="md:col-span-2 flex items-center gap-3">
          <Button type="submit" disabled={busy} data-testid="ti-submit" className="bg-[var(--vs-secondary)] text-[var(--vs-bg)]">{busy ? "Saving…" : "Save trust info"}</Button>
          <label className="text-xs px-3 py-1.5 rounded-md border-2 border-dashed border-[var(--vs-border-focus)]/40 hover:border-[var(--vs-secondary)] cursor-pointer">
            Upload logo (PNG/JPG/SVG ≤ 2MB)
            <input data-testid="ti-logo-input" type="file" accept="image/png,image/jpeg,image/webp,image/svg+xml" className="hidden" onChange={(e) => uploadLogo(e.target.files?.[0])} />
          </label>
        </div>
      </form>
    </div>
  );
}

function HostelSchoolMap() {
  const [hostels, setHostels] = useState([]);
  const [schools, setSchools] = useState([]);
  const [picked, setPicked] = useState("");
  const [mapped, setMapped] = useState([]);
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    api.get("/admin/hostels").then((r) => { setHostels(r.data || []); if (r.data?.[0]) setPicked(r.data[0].id); });
    api.get("/admin/schools").then((r) => setSchools(r.data || []));
  }, []);
  useEffect(() => {
    if (!picked) return;
    api.get(`/admin/hostels/${picked}/schools`).then((r) => setMapped((r.data || []).map((s) => s.id)));
  }, [picked]);
  const toggle = (sid) => setMapped((m) => m.includes(sid) ? m.filter((x) => x !== sid) : [...m, sid]);
  const save = async () => {
    setBusy(true);
    try {
      await api.post(`/admin/hostels/${picked}/schools`, { hostel_id: picked, school_ids: mapped });
      toast.success("Mapping saved");
    } catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail) || e.message); }
    finally { setBusy(false); }
  };
  return (
    <div className="space-y-4 mt-4">
      <div className="vs-card p-4 flex items-center gap-3 flex-wrap">
        <div className="eyebrow flex items-center gap-1.5"><House size={12} /> Pick hostel</div>
        <Select value={picked} onValueChange={setPicked}>
          <SelectTrigger className="w-72" data-testid="hsm-picker"><SelectValue /></SelectTrigger>
          <SelectContent>{hostels.map((h) => <SelectItem key={h.id} value={h.id}>{h.name}</SelectItem>)}</SelectContent>
        </Select>
        <span className="text-xs text-[var(--vs-text-secondary)]">Tick the schools that this hostel feeds into.</span>
      </div>
      <div className="vs-card p-4 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2" data-testid="hsm-options">
        {schools.map((s) => (
          <label key={s.id} className="flex items-center gap-2 p-2 rounded-md bg-[var(--vs-bg-subtle)] cursor-pointer hover:bg-[var(--vs-secondary)]/10">
            <input type="checkbox" checked={mapped.includes(s.id)} onChange={() => toggle(s.id)} data-testid={`hsm-toggle-${s.id}`} /> <span className="text-sm flex items-center gap-1"><GraduationCap size={12} /> {s.name}</span>
          </label>
        ))}
      </div>
      <Button onClick={save} disabled={busy} data-testid="hsm-save" className="bg-[var(--vs-secondary)] text-[var(--vs-bg)]">{busy ? "Saving…" : "Save mapping"}</Button>
    </div>
  );
}

export default function MasterData() {
  return (
    <DashboardLayout title="Master data" subtitle="Academic sessions, fee structure, trust info and hostel ↔ school mapping.">
      <Tabs defaultValue="sessions">
        <TabsList>
          <TabsTrigger value="sessions" data-testid="md-tab-sessions"><GraduationCap size={12} className="mr-1.5" />Academic sessions</TabsTrigger>
          <TabsTrigger value="fees" data-testid="md-tab-fees">Fee configuration</TabsTrigger>
          <TabsTrigger value="trust" data-testid="md-tab-trust"><Buildings size={12} className="mr-1.5" />Trust info</TabsTrigger>
          <TabsTrigger value="mapping" data-testid="md-tab-mapping">Hostel ↔ School mapping</TabsTrigger>
        </TabsList>
        <TabsContent value="sessions"><AcademicSessions /></TabsContent>
        <TabsContent value="fees"><FeeConfigs /></TabsContent>
        <TabsContent value="trust"><TrustInfo /></TabsContent>
        <TabsContent value="mapping"><HostelSchoolMap /></TabsContent>
      </Tabs>
    </DashboardLayout>
  );
}
